import type { AtsEntitySchema, AtsExplorerFilter } from "@/types/ats-explorer-api"

export interface SchemaVariant {
  sampleSize?: number
  filters?: AtsExplorerFilter[]
}

/**
 * Session-level cache for ATS schema discovery, shared by every component that
 * needs live field paths (field path pickers, Reference ID builder, wizard).
 * Schema calls sample live records (one list + several detail fetches against
 * the ATS), so repeated lookups while configuring mappings must not refetch.
 * The promise is cached, so concurrent requests for the same entity dedupe;
 * failures are evicted so a transient error doesn't stick for the session.
 *
 * The sample size and filters are part of the key: the pull wizard samples under
 * the user's filters, and that must not be answered by a picker's unfiltered sample.
 */
const schemaCache = new Map<string, Promise<AtsEntitySchema>>()

export function schemaCacheKey(atsSystem: string, entityType: string, variant: SchemaVariant = {}): string {
  const filters = (variant.filters ?? []).map((f) => `${f.field}|${f.operator}|${f.value}`).sort().join(";")
  return `${atsSystem}:${entityType.toLowerCase()}:${variant.sampleSize ?? 3}:${filters}`
}

export function getCachedSchema(
  atsSystem: string,
  entityType: string,
  fetcher: () => Promise<AtsEntitySchema>,
  variant: SchemaVariant = {}
): Promise<AtsEntitySchema> {
  const key = schemaCacheKey(atsSystem, entityType, variant)

  let cached = schemaCache.get(key)
  if (!cached) {
    cached = fetcher().catch((err) => {
      schemaCache.delete(key)
      throw err
    })
    schemaCache.set(key, cached)
  }

  return cached
}

/** Drops every cached schema, for the hub's Refresh */
export function clearSchemaCache(): void {
  schemaCache.clear()
}
